import React, { useEffect, useState } from 'react';
import { apiGet } from '../lib/api.js';
import { useAuth } from '../store/auth.js';

// Admin-only audit log viewer. Lives at /settings/audit-log.
export default function AuditLog() {
  const { agent } = useAuth();
  const isAdmin = agent?.role === 'admin' || agent?.role === 'superadmin';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ action: '', agent: '', from: '', to: '' });
  const [page, setPage] = useState(0);
  const limit = 50;

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action) params.set('action', filters.action);
      if (filters.agent) params.set('agent', filters.agent);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      params.set('limit', String(limit));
      params.set('offset', String(page * limit));
      const r = await apiGet(`/audit-log?${params.toString()}`);
      setRows(r.rows || []);
      setTotal(r.total || 0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [page]);

  if (!isAdmin) {
    return <div className="p-6 text-white/60">Audit log is admin-only.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-display font-bold mb-1">Audit log</h1>
        <p className="text-white/60 text-sm">Who did what, when. Tenant-scoped, last {limit} per page.</p>
      </div>
      <div className="panel p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <input className="input" placeholder="Action (e.g. lead.view)" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} />
        <input className="input" placeholder="Agent id" value={filters.agent} onChange={(e) => setFilters({ ...filters, agent: e.target.value })} />
        <input className="input" type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
        <input className="input" type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        <button className="btn btn-primary" onClick={() => { setPage(0); load(); }} disabled={loading}>{loading ? 'Loading…' : 'Filter'}</button>
      </div>

      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-white/50 border-b border-civix-border">
            <tr>
              <th className="p-3">When</th>
              <th className="p-3">Actor</th>
              <th className="p-3">Action</th>
              <th className="p-3">Target</th>
              <th className="p-3">IP</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-white/40">No audit events.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r._id} className="border-b border-civix-border/40">
                <td className="p-3 whitespace-nowrap text-white/70">{new Date(r.createdAt || r.ts).toLocaleString()}</td>
                <td className="p-3">{r.actorName || (r.actor || r.agent || '').toString().slice(-6)}</td>
                <td className="p-3 font-mono text-xs">{r.action}</td>
                <td className="p-3 text-xs">{(r.targetType || r.target) ? `${r.targetType || r.target}:${String(r.targetId || '').slice(-6)}` : ''}</td>
                <td className="p-3 text-xs">{r.ip || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between text-sm text-white/60">
        <div>Total: {total}</div>
        <div className="flex gap-2">
          <button className="btn btn-sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Prev</button>
          <button className="btn btn-sm" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= total}>Next</button>
        </div>
      </div>
    </div>
  );
}
